import React, { useState, useEffect, useCallback, useRef, useLayoutEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import EyeIcon from "../../assets/image/eye-icon.svg";
import LeftAngle from "../../assets/image/left-angle.svg";
import RightAngle from "../../assets/image/right-angle.svg";
import Toastify from "../../utils/tostfy";
import { endpoint } from "../../utils/endpoints";
import { normalizeSearchInput, SEARCH_MAX_LENGTH } from "../../utils/searchInput";
import { formatDateTime } from "../../utils/formatDateTime";
import { useListPersistence } from "../../hooks/useListPersistence";
import { readListPage } from "../../hooks/useListQueryPersistence";
import PageNumberInput from "../../Components/Pagination/PageNumberInput";

const LIST_PATH = "/my-requests";

const ACCOUNTANT_API =
  process.env.REACT_APP_ACCOUNTANT_API;

const CHANGE_REQUEST_STATUS = {
  0: "Pending",
  1: "Approved",
  2: "Rejected",
};

const parseBody = async (response) => {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return { rawMessage: text };
  }
};

const getErrorMessage = (payload, response) => {
  if (!payload || typeof payload !== "object") {
    return response.statusText || "Something went wrong. Please try again.";
  }
  return (
    payload.errorMessage ||
    payload.message ||
    payload.error ||
    (Array.isArray(payload.errors) ? payload.errors.join(", ") : null) ||
    `Request failed (${response.status})`
  );
};

const requestTypeToDetailType = (requestType) => {
  const t = String(requestType || "").toLowerCase();
  if (t.includes("debit")) return "debit_note";
  if (t.includes("fuel") || t.includes("waive")) return "fuel_waive";
  if (t.includes("expense") || t.includes("trip")) return "trip_expense";
  return "trip_expense";
};

const mapChangeRequestRow = (r) => {
  const statusNum = r.status == null || r.status === "" ? 0 : Number(r.status);
  return {
    id: r._id,
    requestId: r.request_ref_id ?? "—",
    requestType: r.request_type ?? "—",
    detailType: requestTypeToDetailType(r.request_type),
    submittedDateTime: formatDateTime(r.submitted_at),
    status: CHANGE_REQUEST_STATUS[statusNum] ?? "Pending",
  };
};

const MyRequests = () => {
  const [searchParams] = useSearchParams();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalRecords, setTotalRecords] = useState(0);
  const [apiTotalPages, setApiTotalPages] = useState(1);

  const [searchInput, setSearchInput] = useState(() => searchParams.get("search") || "");
  const [search, setSearch] = useState(() => (searchParams.get("search") || "").trim());
  const [currentPage, setCurrentPage] = useState(() => readListPage(searchParams));
  const itemsPerPage = 10;
  const skipSearchResetRef = useRef(false);

  const applyRestoredState = useCallback((saved) => {
    skipSearchResetRef.current = true;
    if (saved.search != null) {
      setSearchInput(saved.search);
      setSearch(String(saved.search).trim());
    }
    if (saved.page) {
      const p = Number(saved.page);
      if (Number.isFinite(p) && p > 0) setCurrentPage(p);
    }
    window.setTimeout(() => {
      skipSearchResetRef.current = false;
    }, 0);
  }, []);

  const { detailListQuery } = useListPersistence(
    LIST_PATH,
    {
      page: currentPage,
      search: search || undefined,
    },
    applyRestoredState
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      if (!skipSearchResetRef.current) setCurrentPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    let cancelled = false;

    const loadRequests = async () => {
      const token = localStorage.getItem("sessionToken");
      if (!token) {
        toast.error("Session expired. Please log in again.");
        setRequests([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      const url = `${ACCOUNTANT_API}${endpoint.listOfChangeRequest}?search=${encodeURIComponent(
        search
      )}&page=${currentPage}&limit=${itemsPerPage}`;

      try {
        const response = await fetch(url, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const payload = await parseBody(response);

        if (cancelled) return;

        if (!response.ok) {
          toast.error(getErrorMessage(payload, response));
          setRequests([]);
          setTotalRecords(0);
          setApiTotalPages(1);
          return;
        }

        const apiCode = payload?.code;
        if (apiCode != null && apiCode !== 200 && apiCode !== 201) {
          toast.error(getErrorMessage(payload, response));
          setRequests([]);
          setTotalRecords(0);
          setApiTotalPages(1);
          return;
        }

        const data = payload?.data || {};
        const list = Array.isArray(data.change_request_data)
          ? data.change_request_data
          : [];

        setRequests(list.map(mapChangeRequestRow));
        setTotalRecords(typeof data.total === "number" ? data.total : list.length);
        setApiTotalPages(
          typeof data.total_pages === "number" && data.total_pages > 0
            ? data.total_pages
            : 1
        );
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          toast.error(
            err?.message === "Failed to fetch"
              ? "Network error. Check your connection."
              : "Could not load change requests."
          );
          setRequests([]);
          setTotalRecords(0);
          setApiTotalPages(1);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadRequests();
    return () => {
      cancelled = true;
    };
  }, [currentPage, search]);

  const getStatusStyle = (status) => {
    switch (status) {
      case "Pending":
        return "text-yellow-600";
      case "Approved":
        return "text-green-600";
      case "Rejected":
        return "text-red-600";
      default:
        return "";
    }
  };

  const totalPages = apiTotalPages;
  const serialBase = (currentPage - 1) * itemsPerPage;

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <div className="">
        <div className="flex mb-3">
          <input
            type="text"
            placeholder="Search"
            className="form-control max-w-60"
            value={searchInput}
            maxLength={SEARCH_MAX_LENGTH}
            onChange={(e) => setSearchInput(normalizeSearchInput(e.target.value))}
          />
          <Link
            to="/my-requests/add-new-request"
            className="btn btn-danger ml-auto"
          >
            Create New Request
          </Link>
        </div>
        <div className="common-table-wrapper m-0 shadow-sm">
          <div className="table-responsive">
            <table className="w-full text-sm">
              <thead className="bg-gray-700 text-white">
                <tr>
                  <th className="p-3 text-left">S.No.</th>
                  <th className="p-3 text-left">Request ID</th>
                  <th className="p-3 text-left">Request Type</th>
                  <th className="p-3 text-left">Submitted Date & Time</th>
                  <th className="p-3 text-left">Status</th>
                  <th className="p-3 text-center">Action</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} className="p-8 text-center text-gray-500">
                      Loading requests…
                    </td>
                  </tr>
                ) : requests.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="p-8 text-center text-gray-500">
                      No requests found.
                    </td>
                  </tr>
                ) : (
                  requests.map((item, index) => (
                    <tr key={item.id} className="border-b hover:bg-gray-50">
                      <td>{String(serialBase + index + 1).padStart(2, "0")}</td>
                      <td>{item.requestId}</td>
                      <td>{item.requestType}</td>
                      <td>{item.submittedDateTime}</td>
                      <td
                        className={`font-medium ${getStatusStyle(item.status)}`}
                      >
                        {item.status}
                      </td>
                      <td className="text-center">
                        <Link
                          to={`/my-requests/view-request-retails?request_id=${encodeURIComponent(
                            item.id
                          )}&type=${encodeURIComponent(
                            item.detailType || "trip_expense"
                          )}${detailListQuery}`}
                        >
                          <img
                            src={EyeIcon}
                            className="inline-block h-7 min-w-[22px]"
                            alt="View details"
                          />
                        </Link>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex justify-end items-center gap-4 p-4 text-sm">
          <span className="text-gray-500">{totalRecords} records found</span>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1 || loading}
              className="!bg-primary text-white w-[35px] h-[35px] rounded-full disabled:opacity-50 flex items-center justify-center"
            >
              <img src={LeftAngle} className="invert" alt="" />
            </button>

            <PageNumberInput
              page={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              disabled={loading}
            />

            <span className="text-gray-500">of {totalPages}</span>

            <button
              type="button"
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={currentPage === totalPages || loading}
              className="!bg-primary text-white w-[35px] h-[35px] rounded-full disabled:opacity-50 flex items-center justify-center"
            >
              <img src={RightAngle} className="invert" alt="" />
            </button>
          </div>
        </div>
      </div>

      <Toastify />
    </div>
  );
};

export default MyRequests;
